import { useState, useEffect } from 'react';
import { getIdTokenClaims, resolveUserEmail } from '../../../Utils/auth';
import { useAuth } from '../../../context/AuthContext';

export const useUserInfo = () => {
  const [email, setEmail] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string>('');
  const { user } = useAuth();

  const resolveEmail = async (): Promise<string | null> => {
    const cached = sessionStorage.getItem('userEmail');
    if (cached) {
      setEmail(cached);
      return cached;
    }
    const em = await resolveUserEmail();
    if (em) {
      setEmail(em);
      sessionStorage.setItem('userEmail', em);
    }
    return em;
  };


  // This hook resolves and caches the user’s email from auth tokens, and builds a 
  // reliable display name using stored profile info → token claims → email fallback → userId fallback.
  useEffect(() => {
    const storedAuth = sessionStorage.getItem('auth') || localStorage.getItem('auth');

    let name = '';

    try {
      if (storedAuth) {
        const parsed = JSON.parse(storedAuth);
        const profile = parsed?.user?.profile || {};
        if (profile.firstName && profile.lastName) {
          name = `${profile.firstName} ${profile.lastName}`;
        } else {
          name = profile.firstName?.trim?.() || profile.username?.trim?.() || '';
        }
      }
    } catch {
      // ignore JSON parse errors
    }

    const pullFromIdToken = async () => {
      const claims: any = (await getIdTokenClaims()) || {};
      return (
        (claims.given_name && claims.family_name && `${claims.given_name} ${claims.family_name}`.trim()) ||
        claims.given_name?.trim?.() ||
        claims.name?.trim?.() ||
        claims.preferred_username?.trim?.() ||
        ''
      );
    };

    const finalize = async () => {
      if (!name) {
        name = await pullFromIdToken();
      }
      if (!name && email) name = email.split('@')[0];
      if (!name && user?.userId) name = String(user.userId);
      setDisplayName(name || '');
    };

    void finalize();
  }, [email, user]);

  return { email, displayName, resolveEmail };
};